import { inject } from '@angular/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from "@ngrx/operators";
import { concatMap, iif, of, pipe, switchMap, tap } from 'rxjs';
import { ErrorHandlerService } from '@app-services/error-handler.service';
import { HttpErrorResponse } from '@angular/common/http';
import { IUserPhoto, TUploadFile } from '@shared/components/user-photo/data-access/entities/user-photo.interface';
import { UserPhotosService } from './infra/user-photos.service';
import { AlertStore } from '@shared/components/alert/data-access/alert.store';
import { setPropError, setPropInit, setPropLoaded, setPropLoading, withReqState } from '@shared/stores/prop-state.store';
import { EAlertTypes } from '@shared/components/alert/data-access/entities/alert.enum';
import { eStorageFolder } from './entities/user-photo.enum';

interface IUserPhotoState {
    UserPhotoById: Record<string, IUserPhoto | null>;
    UserPhotoByIdState: Record<string, 'loading' | 'loaded' | 'error'>;
    UploadedFile: TUploadFile | null;
};

const initialState: IUserPhotoState = {
    UserPhotoById: {},
    UserPhotoByIdState: {},
    UploadedFile: null
};

export const UserPhotoStore = signalStore(
    { providedIn: 'root' },
    withState(initialState),
    withMethods((
        store,
        alertStore = inject(AlertStore),
        userPhotosService = inject(UserPhotosService),
        errorHandlerService = inject(ErrorHandlerService),
    ) => {

        const setFlagAsyncToTreino = rxMethod<IUserPhoto>(pipe(
            switchMap((userPhoto) => userPhotosService.setFlagAsyncToTreino(String(userPhoto.FileId)).pipe(
                tapResponse({
                    next: () => {
                        alertStore.openAlert({
                            type: EAlertTypes.SUCCESS,
                            title: 'Alteração realizada!',
                            message: 'Sua foto foi atualizada com sucesso.',
                        });
                        userPhoto.File.Url = `${userPhoto.File.Url}?${new Date().getTime()}`;
                        patchState(store, { UserPhotoById: { ...store.UserPhotoById(), [userPhoto.PersonId || '']: userPhoto }, UserPhotoByIdState: { ...store.UserPhotoByIdState(), [userPhoto.PersonId!]: 'loaded' } });
                    },
                    error: (error: HttpErrorResponse) => {
                        patchState(store, { UserPhotoByIdState: { ...store.UserPhotoByIdState(), [userPhoto.PersonId!]: 'error' } });
                        errorHandlerService.handleError(error);
                    }
                })
            ))
        ));

        const base64toFile = (base64: string, filename: any ) => {
            const match = base64.match(/^data:(image\/[a-z]+);base64,/);
            let mimeType = 'image/jpeg';
            let base64Data = base64;

            if (match) {
                mimeType = match[1];
                base64Data = base64.replace(/^data:image\/[a-z]+;base64,/, '');
                } else {
                const extension = filename?.split('.')?.pop()?.toLowerCase();
                switch (extension) {
                    case 'png':
                    mimeType = 'image/png';
                    break;
                    case 'gif':
                    mimeType = 'image/gif';
                    break;
                    case 'webp':
                    mimeType = 'image/webp';
                    break;
                    default:
                    mimeType = 'image/jpeg';
                }
            }

            const byteString = atob(base64Data);
            const arrayBuffer = new ArrayBuffer(byteString.length);
            const int8Array = new Uint8Array(arrayBuffer);

            for (let i = 0; i < byteString.length; i++) {
            int8Array[i] = byteString.charCodeAt(i);
            }

            const imageBlob = new Blob([int8Array], { type: mimeType });
            const file = new File([imageBlob], filename, { type: mimeType });

            return file;
        };

        return {
            getUserPhotoById: rxMethod<string>(pipe(
                concatMap((userId) => iif(
                    () => {
                        const canRequest = !store.UserPhotoByIdState()[userId] || store.UserPhotoByIdState()[userId] === 'error';
                        if (canRequest) patchState(store, { UserPhotoByIdState: { ...store.UserPhotoByIdState(), [userId]: 'loading' } });
                        return canRequest;
                    },
                    userPhotosService.getUserPhotosById(userId).pipe(
                        tapResponse({
                            next: (userPhotos) => {
                                patchState(store, { UserPhotoById: { ...store.UserPhotoById(), [userId]: userPhotos[0] ?? {} }, UserPhotoByIdState: { ...store.UserPhotoByIdState(), [userId]: 'loaded' } });
                            },
                            error: (error: HttpErrorResponse) => {
                                patchState(store, { UserPhotoByIdState: { ...store.UserPhotoByIdState(), [userId]: 'error' } });
                                // errorHandlerService.handleError(error);
                            }
                        }),
                    ),
                    of(null)
                ))
            )),

            newUserPhoto: rxMethod<IUserPhoto>(pipe(
                tap((userPhoto) => patchState(store, { UserPhotoByIdState: { ...store.UserPhotoByIdState(), [userPhoto.PersonId!]: 'loading' } })),
                switchMap((userPhoto) => {
                    const request = userPhoto.FileId ? userPhotosService.putUserPhoto(userPhoto) : userPhotosService.postUserPhoto(userPhoto);

                    return request.pipe(
                        tapResponse({
                            next: (userPhoto) => setFlagAsyncToTreino(userPhoto),
                            error: (error: HttpErrorResponse) => {
                                patchState(store, { UserPhotoByIdState: { ...store.UserPhotoByIdState(), [userPhoto.PersonId!]: 'error' } });
                                errorHandlerService.handleError(error);
                            }
                        })
                    )
                })
            )),

            uploadFile: rxMethod<{ base64: any, folder: eStorageFolder, filename?: string }>(
                pipe(
                    tap(() => patchState(store, { ...setPropLoading('uploadFile') })),
                    switchMap(({ base64, folder, filename }) =>
                    userPhotosService.uploadFile(base64toFile(base64, filename), folder).pipe(
                        tapResponse({
                        next: (UploadedFile) => {
                            patchState(store, { UploadedFile, ...setPropLoaded('uploadFile') });
                        },
                        error: (error: HttpErrorResponse) => {
                            console.error(error);
                            errorHandlerService.handleError(error);
                            patchState(store, { ...setPropError('uploadFile', error) });
                        },
                        })
                    ),
                    )
                )
            ),
            setUploadFileBlankState: () => {
                patchState(store, { UploadedFile: null, ...setPropInit('uploadFile') });
            },

            resetStore(): void {
                patchState(store, initialState);
            }
        };
    }),
    withReqState('uploadFile')
);